import React, { useState, useEffect } from 'react';
import {
  Row, Col, Spin, Alert, Table, Tag, Space, Card,
  Button, Select, Modal, Form, Input, DatePicker, Empty,
  Tabs, message, Statistic, Avatar, Tooltip, Badge, List,
  Dropdown, Checkbox
} from 'antd';
import {
  CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined,
  PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined,
  CommentOutlined, MoreOutlined, FilterOutlined, SearchOutlined,
  ReloadOutlined, ProjectOutlined, FireOutlined, CloseCircleOutlined,
  RightOutlined, CaretRightOutlined, SyncOutlined
} from '@ant-design/icons';
import { taskService, userService } from '../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import '../styles/Phase5.css';

dayjs.extend(relativeTime);

const { Option } = Select;
const { TextArea } = Input;

const Tasks = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [kanbanData, setKanbanData] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);

  const [viewMode, setViewMode] = useState('kanban'); // kanban, list
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [filters, setFilters] = useState({
    my_tasks: false,
    status: null,
    priority: null,
  });

  const [taskForm] = Form.useForm();
  const [commentInput, setCommentInput] = useState('');

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, kanbanDataResp, usersData, activityData] = await Promise.all([
        taskService.getStats().catch(() => null),
        taskService.getKanbanBoard(filters).catch(() => null),
        userService.getAll().catch(() => []),
        taskService.getRecentActivity(10).catch(() => []),
      ]);

      setStats(statsData);
      setKanbanData(kanbanDataResp);
      setUsers(usersData || []);
      setActivity(activityData || []);

      // Also fetch list view data
      const tasksData = await taskService.getAll({ ...filters, limit: 100 }).catch(() => []);
      setTasks(tasksData || []);

    } catch (err) {
      console.error('Error fetching task data:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (values) => {
    setActionLoading(true);
    try {
      await taskService.create({
        ...values,
        due_date: values.due_date?.toISOString(),
      });
      message.success('Task created successfully');
      setTaskModalVisible(false);
      taskForm.resetFields();
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.detail || 'Failed to create task');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      await taskService.update(taskId, updates);
      message.success('Task updated');
      fetchData();
      if (selectedTask?.id === taskId) {
        const updated = await taskService.getById(taskId);
        setSelectedTask(updated);
      }
    } catch (err) {
      message.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    Modal.confirm({
      title: 'Delete Task',
      content: 'Are you sure you want to delete this task?',
      onOk: async () => {
        try {
          await taskService.delete(taskId);
          message.success('Task deleted');
          setDetailModalVisible(false);
          setSelectedTask(null);
          fetchData();
        } catch (err) {
          message.error('Failed to delete task');
        }
      },
    });
  };

  const handleAddComment = async () => {
    if (!commentInput.trim() || !selectedTask) return;
    setActionLoading(true);
    try {
      await taskService.addComment(selectedTask.id, commentInput);
      setCommentInput('');
      const updated = await taskService.getById(selectedTask.id);
      setSelectedTask(updated);
      message.success('Comment added');
    } catch (err) {
      message.error('Failed to add comment');
    } finally {
      setActionLoading(false);
    }
  };

  const openTaskDetail = (task) => {
    setSelectedTask(task);
    setDetailModalVisible(true);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'red',
      high: 'orange',
      medium: 'blue',
      low: 'default',
    };
    return colors[priority] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      todo: 'default',
      in_progress: 'processing',
      review: 'warning',
      done: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const getPriorityIcon = (priority) => {
    if (priority === 'critical') return <FireOutlined style={{ color: '#ef4444' }} />;
    if (priority === 'high') return <ExclamationCircleOutlined style={{ color: '#f97316' }} />;
    return null;
  };

  // Kanban Card Component
  const TaskCard = ({ task }) => {
    const isOverdue = task.due_date && dayjs(task.due_date).isBefore(dayjs()) && task.status !== 'done';

    return (
      <div
        className="p5-card"
        style={{
          marginBottom: 12,
          cursor: 'pointer',
          borderLeft: `4px solid ${
            task.priority === 'critical' ? '#ef4444' :
            task.priority === 'high' ? '#f97316' :
            task.priority === 'medium' ? '#3b82f6' : '#94a3b8'
          }`,
        }}
        onClick={() => openTaskDetail(task)}
      >
        <div className="p5-card-body" style={{ padding: 12 }}>
          <div className="p5-flex-between" style={{ marginBottom: 8 }}>
            <Space>
              {getPriorityIcon(task.priority)}
              <Tag color={getPriorityColor(task.priority)} style={{ margin: 0 }}>
                {task.priority?.toUpperCase()}
              </Tag>
            </Space>
            <Dropdown menu={{
              items: [
                { key: 'edit', label: 'Edit', icon: <EditOutlined /> },
                { key: 'delete', label: 'Delete', icon: <DeleteOutlined />, danger: true },
              ],
              onClick: (e) => {
                e.domEvent.stopPropagation();
                if (e.key === 'delete') handleDeleteTask(task.id);
              }
            }}>
              <Button type="text" size="small" icon={<MoreOutlined />} onClick={e => e.stopPropagation()} />
            </Dropdown>
          </div>

          <div style={{ fontWeight: 500, marginBottom: 8, color: 'var(--p5-navy-800)' }}>
            {task.title}
          </div>

          {task.description && (
            <div style={{ fontSize: '0.8rem', color: 'var(--p5-slate-500)', marginBottom: 8 }}>
              {task.description.length > 80 ? task.description.substring(0, 80) + '...' : task.description}
            </div>
          )}

          <div className="p5-flex-between" style={{ marginTop: 8 }}>
            <Space>
              {task.assigned_to ? (
                <Tooltip title={task.assigned_to.full_name}>
                  <Avatar size="small" style={{ backgroundColor: '#f59e0b' }}>
                    {task.assigned_to.full_name?.charAt(0) || 'U'}
                  </Avatar>
                </Tooltip>
              ) : (
                <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#94a3b8' }} />
              )}
              {task.comments?.length > 0 && (
                <Badge count={task.comments.length} size="small">
                  <CommentOutlined style={{ color: '#64748b' }} />
                </Badge>
              )}
            </Space>
            {task.due_date && (
              <Tag color={isOverdue ? 'red' : 'default'} style={{ margin: 0, fontSize: '0.75rem' }}>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                {dayjs(task.due_date).format('MMM D')}
              </Tag>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Kanban Column Component
  const KanbanColumn = ({ column }) => {
    const statusIcons = {
      todo: <ClockCircleOutlined />,
      in_progress: <SyncOutlined spin />,
      review: <ExclamationCircleOutlined />,
      done: <CheckCircleOutlined />,
    };

    return (
      <div style={{
        background: 'var(--p5-slate-100)',
        borderRadius: 'var(--p5-radius-md)',
        padding: 16,
        height: '100%',
        minHeight: 400,
      }}>
        <div className="p5-flex-between" style={{ marginBottom: 16 }}>
          <Space>
            {statusIcons[column.key]}
            <span style={{ fontWeight: 600 }}>{column.title}</span>
            <Badge count={column.tasks?.length || 0} style={{ backgroundColor: '#64748b' }} />
          </Space>
        </div>
        <div style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
          {column.tasks?.map(task => (
            <TaskCard key={task.id} task={task} />
          ))}
          {column.tasks?.length === 0 && (
            <Empty description="No tasks" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </div>
      </div>
    );
  };

  const tableColumns = [
    {
      title: 'Task',
      key: 'title',
      render: (_, record) => (
        <div>
          <Space>
            {getPriorityIcon(record.priority)}
            <span style={{ fontWeight: 500 }}>{record.title}</span>
          </Space>
          {record.related_entity_type && (
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
              Linked to: {record.related_entity_type}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status?.replace('_', ' ').toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>{priority?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Assignee',
      key: 'assigned_to',
      width: 150,
      render: (_, record) => record.assigned_to ? (
        <Space>
          <Avatar size="small" style={{ backgroundColor: '#f59e0b' }}>
            {record.assigned_to.full_name?.charAt(0)}
          </Avatar>
          {record.assigned_to.full_name}
        </Space>
      ) : <span style={{ color: '#94a3b8' }}>Unassigned</span>,
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 120,
      render: (date, record) => {
        if (!date) return '-';
        const isOverdue = dayjs(date).isBefore(dayjs()) && record.status !== 'done';
        return (
          <Tag color={isOverdue ? 'red' : 'default'}>
            {dayjs(date).format('MMM D, YYYY')}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => openTaskDetail(record)} />
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteTask(record.id)} />
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'kanban',
      label: (
        <span className="p5-flex p5-gap-sm">
          <ProjectOutlined />
          Kanban Board
        </span>
      ),
      children: (
        <div className="p5-animate-fadeUp">
          {kanbanData?.columns ? (
            <Row gutter={16}>
              {kanbanData.columns.map(col => (
                <Col xs={24} sm={12} lg={6} key={col.key}>
                  <KanbanColumn column={col} />
                </Col>
              ))}
            </Row>
          ) : (
            <Empty description="No tasks found" />
          )}
        </div>
      ),
    },
    {
      key: 'list',
      label: (
        <span className="p5-flex p5-gap-sm">
          <ProjectOutlined />
          List View
        </span>
      ),
      children: (
        <div className="p5-table-wrapper p5-animate-fadeUp">
          <Table
            dataSource={tasks}
            columns={tableColumns}
            rowKey="id"
            pagination={{ pageSize: 20 }}
            onRow={(record) => ({
              onClick: () => openTaskDetail(record),
              style: { cursor: 'pointer' }
            })}
          />
        </div>
      ),
    },
    {
      key: 'activity',
      label: (
        <span className="p5-flex p5-gap-sm">
          <ClockCircleOutlined />
          Activity
        </span>
      ),
      children: (
        <div className="p5-card p5-animate-fadeUp">
          <div className="p5-card-body">
            <List
              dataSource={activity}
              renderItem={(item) => (
                <List.Item style={{ borderBottom: '1px solid var(--p5-slate-100)' }}>
                  <List.Item.Meta
                    avatar={
                      <Avatar style={{ backgroundColor: item.type === 'comment' ? '#3b82f6' : '#f59e0b' }}>
                        {item.type === 'comment' ? <CommentOutlined /> : <CheckCircleOutlined />}
                      </Avatar>
                    }
                    title={
                      <Space>
                        <span style={{ fontWeight: 500 }}>{item.task_title}</span>
                        {item.type === 'task_update' && (
                          <Tag color={getStatusColor(item.task_status)}>{item.task_status}</Tag>
                        )}
                      </Space>
                    }
                    description={
                      <div>
                        {item.type === 'comment' && (
                          <div style={{ marginBottom: 4 }}>{item.comment_preview}</div>
                        )}
                        <Space style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          {item.user && <span>by {item.user}</span>}
                          <span>{dayjs(item.timestamp).fromNow()}</span>
                        </Space>
                      </div>
                    }
                  />
                </List.Item>
              )}
              locale={{ emptyText: 'No recent activity' }}
            />
          </div>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="p5-page">
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  return (
    <div className="p5-page">
      {/* Page Header */}
      <div className="p5-page-header p5-flex-between" style={{ alignItems: 'flex-start' }}>
        <div>
          <h1 className="p5-page-title">Task Management</h1>
          <p className="p5-page-subtitle">
            Track, assign, and collaborate on procurement tasks and action items
          </p>
        </div>
        <Space>
          <Checkbox
            checked={filters.my_tasks}
            onChange={(e) => setFilters({ ...filters, my_tasks: e.target.checked })}
          >
            My Tasks
          </Checkbox>
          <Select
            placeholder="Status"
            style={{ width: 130 }}
            allowClear
            value={filters.status}
            onChange={(val) => setFilters({ ...filters, status: val })}
          >
            <Option value="todo">To Do</Option>
            <Option value="in_progress">In Progress</Option>
            <Option value="review">Review</Option>
            <Option value="done">Done</Option>
          </Select>
          <Select
            placeholder="Priority"
            style={{ width: 120 }}
            allowClear
            value={filters.priority}
            onChange={(val) => setFilters({ ...filters, priority: val })}
          >
            <Option value="critical">Critical</Option>
            <Option value="high">High</Option>
            <Option value="medium">Medium</Option>
            <Option value="low">Low</Option>
          </Select>
          <Button
            className="p5-btn p5-btn--ghost"
            icon={<ReloadOutlined />}
            onClick={fetchData}
          >
            Refresh
          </Button>
          <Button
            className="p5-btn p5-btn--accent"
            icon={<PlusOutlined />}
            onClick={() => setTaskModalVisible(true)}
          >
            New Task
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {/* Stats Grid */}
        <div className="p5-stats-grid p5-stagger">
          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--amber">
              <ProjectOutlined />
            </div>
            <div className="p5-stat-label">Total Tasks</div>
            <div className="p5-stat-value">{stats?.total_tasks || 0}</div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--teal">
              <UserOutlined />
            </div>
            <div className="p5-stat-label">My Pending</div>
            <div className="p5-stat-value">{stats?.my_pending || 0}</div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--rose">
              <ExclamationCircleOutlined />
            </div>
            <div className="p5-stat-label">Overdue</div>
            <div className="p5-stat-value" style={{ color: (stats?.overdue || 0) > 0 ? 'var(--p5-rose-500)' : undefined }}>
              {stats?.overdue || 0}
            </div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--emerald">
              <CheckCircleOutlined />
            </div>
            <div className="p5-stat-label">Completed This Week</div>
            <div className="p5-stat-value">{stats?.completed_this_week || 0}</div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          defaultActiveKey="kanban"
          items={tabItems}
          className="p5-tabs"
          onChange={(key) => setViewMode(key)}
        />
      </Spin>

      {/* Create Task Modal */}
      <Modal
        title="Create New Task"
        open={taskModalVisible}
        onCancel={() => { setTaskModalVisible(false); taskForm.resetFields(); }}
        footer={null}
        width={600}
      >
        <Form form={taskForm} layout="vertical" onFinish={handleCreateTask}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Enter task title' }]}
          >
            <Input placeholder="Task title" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Task description..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="priority" label="Priority" initialValue="medium">
                <Select>
                  <Option value="critical"><FireOutlined /> Critical</Option>
                  <Option value="high"><ExclamationCircleOutlined /> High</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="low">Low</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status" initialValue="todo">
                <Select>
                  <Option value="todo">To Do</Option>
                  <Option value="in_progress">In Progress</Option>
                  <Option value="review">Review</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="assigned_to_user_id" label="Assign To">
                <Select placeholder="Select user" allowClear>
                  {users.map(u => (
                    <Option key={u.id} value={u.id}>{u.full_name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="due_date" label="Due Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={actionLoading} block>
              Create Task
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Task Detail Modal */}
      <Modal
        title={null}
        open={detailModalVisible}
        onCancel={() => { setDetailModalVisible(false); setSelectedTask(null); setCommentInput(''); }}
        footer={null}
        width={700}
        className="p5-modal"
      >
        {selectedTask && (
          <>
            <div style={{
              background: 'var(--p5-navy-900)',
              margin: '-24px -24px 24px',
              padding: '24px',
              borderRadius: 'var(--p5-radius-lg) var(--p5-radius-lg) 0 0'
            }}>
              <div className="p5-flex-between" style={{ marginBottom: 12 }}>
                <Space>
                  <Tag color={getPriorityColor(selectedTask.priority)}>
                    {selectedTask.priority?.toUpperCase()}
                  </Tag>
                  <Tag color={getStatusColor(selectedTask.status)}>
                    {selectedTask.status?.replace('_', ' ').toUpperCase()}
                  </Tag>
                </Space>
                <Space>
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    danger
                    onClick={() => handleDeleteTask(selectedTask.id)}
                    style={{ color: 'rgba(255,255,255,0.7)' }}
                  />
                </Space>
              </div>
              <h2 className="p5-font-display" style={{ fontSize: '1.5rem', color: '#fff', margin: 0 }}>
                {selectedTask.title}
              </h2>
            </div>

            {selectedTask.description && (
              <div style={{ marginBottom: 24 }}>
                <div className="p5-label" style={{ marginBottom: 8 }}>Description</div>
                <p style={{ color: 'var(--p5-navy-600)', margin: 0 }}>{selectedTask.description}</p>
              </div>
            )}

            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <div className="p5-label">Status</div>
                <Select
                  value={selectedTask.status}
                  style={{ width: '100%' }}
                  onChange={(val) => handleUpdateTask(selectedTask.id, { status: val })}
                >
                  <Option value="todo">To Do</Option>
                  <Option value="in_progress">In Progress</Option>
                  <Option value="review">Review</Option>
                  <Option value="done">Done</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Col>
              <Col span={8}>
                <div className="p5-label">Priority</div>
                <Select
                  value={selectedTask.priority}
                  style={{ width: '100%' }}
                  onChange={(val) => handleUpdateTask(selectedTask.id, { priority: val })}
                >
                  <Option value="critical">Critical</Option>
                  <Option value="high">High</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="low">Low</Option>
                </Select>
              </Col>
              <Col span={8}>
                <div className="p5-label">Assignee</div>
                <Select
                  value={selectedTask.assigned_to_user_id}
                  style={{ width: '100%' }}
                  allowClear
                  placeholder="Unassigned"
                  onChange={(val) => handleUpdateTask(selectedTask.id, { assigned_to_user_id: val })}
                >
                  {users.map(u => (
                    <Option key={u.id} value={u.id}>{u.full_name}</Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <div className="p5-label">Due Date</div>
                <DatePicker
                  value={selectedTask.due_date ? dayjs(selectedTask.due_date) : null}
                  style={{ width: '100%' }}
                  onChange={(date) => handleUpdateTask(selectedTask.id, { due_date: date?.toISOString() })}
                />
              </Col>
              <Col span={12}>
                <div className="p5-label">Created</div>
                <div style={{ padding: '8px 0', color: 'var(--p5-slate-500)' }}>
                  {dayjs(selectedTask.created_at).format('MMM D, YYYY')}
                  {selectedTask.created_by && ` by ${selectedTask.created_by.full_name}`}
                </div>
              </Col>
            </Row>

            {/* Comments */}
            <div style={{ borderTop: '1px solid var(--p5-slate-200)', paddingTop: 24 }}>
              <h4 style={{ marginBottom: 16 }}>
                <CommentOutlined /> Comments ({selectedTask.comments?.length || 0})
              </h4>

              {selectedTask.comments?.length > 0 && (
                <List
                  dataSource={selectedTask.comments}
                  renderItem={(comment) => (
                    <List.Item style={{ paddingLeft: 0, paddingRight: 0 }}>
                      <List.Item.Meta
                        avatar={
                          <Avatar style={{ backgroundColor: '#f59e0b' }}>
                            {comment.user?.full_name?.charAt(0) || 'U'}
                          </Avatar>
                        }
                        title={
                          <Space>
                            <span style={{ fontWeight: 500 }}>{comment.user?.full_name}</span>
                            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                              {dayjs(comment.created_at).fromNow()}
                            </span>
                          </Space>
                        }
                        description={comment.content}
                      />
                    </List.Item>
                  )}
                  style={{ marginBottom: 16 }}
                />
              )}

              <div style={{ display: 'flex', gap: 12 }}>
                <Input.TextArea
                  value={commentInput}
                  onChange={(e) => setCommentInput(e.target.value)}
                  placeholder="Add a comment..."
                  rows={2}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  onClick={handleAddComment}
                  loading={actionLoading}
                  disabled={!commentInput.trim()}
                >
                  Post
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Tasks;
